import { useMutation } from "@tanstack/react-query";
import { solicitudesService } from "app/services";
import { createContext, useState, useContext } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useDeepCompareEffect from "use-deep-compare-effect";

const EmpresaDataContext = createContext();

const initialState = {
  denominacion_social_id: 0,
  empresa_nombre_comercial: "",
  empresa_rtn: "",
  representacion_geografica_id: 0,
  empresa_cantidad_de_empleados: 0,
  camara_de_comercio_id: 0,
  registro_mercantil_ciudad_id: 0,
  registro_mercantil_fecha: "",
  registro_mercantil_matricula: "",
  registro_mercantil_inscripcion: "",
  registro_mercantil_tomo: "",
  decreto_legislativo_id: 0,
  registro_mercantil_capital_autorizado: 0,
  telefonos: [],
  celulares: [],
  correos_electronicos: [],
  paginas_web: [],
  establecimientos: [],
  encargados: [],
  actividades_economicas: [],
  importaciones: [],
  exportaciones: [],
  empresa_afiliada: false,
  forma_de_pago_id: 1,
  frecuencia_de_pago_id: 1,
};

export function EmpresaDataProvider(props) {
  const [empresa, setEmpresa] = useState(() => {
    const stored = JSON.parse(window.localStorage.getItem("empresa"));

    return { ...initialState, ...stored };
  });

  const navigate = useNavigate();

  const createSolicitudMutation = useMutation({
    queryKey: ["create-solicitud"],
    mutationFn: ({ files = [] }) =>
      toast.promise(solicitudesService.createSolicitud({ empresa, files }), {
        loading: "Enviando solicitud de inscripción, por favor, espere...",
        success: "!Solicitud de inscripción enviada existosamente!",
        error: (error) => error.response.statusText,
      }),
    onSuccess: (data) => {
      window.open(URL.createObjectURL(data));

      setEmpresa(initialState);

      return navigate("/");
    },
  });

  useDeepCompareEffect(() => {
    try {
      window.localStorage.setItem("empresa", JSON.stringify(empresa));
    } catch (error) {
      console.log(error.message);
    }
  }, [empresa]);

  // establecimientos
  function addEstablecimiento(establecimiento) {
    const already = empresa.establecimientos.find((e) => {
      return (
        e.ciudad_id === establecimiento.ciudad_id &&
        String(e.establecimiento_direccion_detallada).toLowerCase() ===
          String(
            establecimiento.establecimiento_direccion_detallada
          ).toLowerCase()
      );
    });

    if (!!already) {
      throw new Error("¡Este establecimiento ya está agregado!");
    }

    const establecimientos = empresa.establecimientos.concat(establecimiento);

    setEmpresa((empresa) => ({
      ...empresa,
      establecimientos,
    }));

    return true;
  }

  function getEstablecimiento(index) {
    const establecimiento = empresa.establecimientos.find(
      (e, i) => i === index
    );

    if (!establecimiento) {
      throw new Error("¡Este establecimiento no existe!");
    }

    return establecimiento;
  }

  function updateEstablecimiento(index, establecimiento) {
    const already = empresa.establecimientos.find((e, i) => {
      return (
        i !== index &&
        e.ciudad_id === establecimiento.ciudad_id &&
        String(e.establecimiento_direccion_detallada).toLowerCase() ===
          String(
            establecimiento.establecimiento_direccion_detallada
          ).toLowerCase()
      );
    });

    if (!!already) {
      throw new Error("¡Este establecimiento ya está agregado!");
    }

    const establecimientos = empresa.establecimientos.map((e, i) => {
      if (i === index) {
        return establecimiento;
      }

      return e;
    });

    setEmpresa((empresa) => ({
      ...empresa,
      establecimientos,
    }));

    return true;
  }

  function deleteEstablecimiento(index) {
    if (
      window.confirm("¿Está seguro(a) que desea eliminar este establecimiento?")
    ) {
      const establecimientos = empresa.establecimientos.filter(
        (e, i) => index !== i
      );

      setEmpresa((empresa) => ({
        ...empresa,
        establecimientos,
      }));

      return true;
    }

    return false;
  }

  // números de teléfono
  function addTelefono(telefono) {
    const already = empresa.telefonos.find(
      (tel) =>
        String(tel.telefono_numero).replaceAll("-", "") ===
        String(telefono.telefono_numero).replaceAll("-", "")
    );

    if (!!already) {
      throw new Error("¡Este número de teléfono ya está agregado!");
    }

    const telefonos = empresa.telefonos.concat(telefono);

    setEmpresa((empresa) => ({
      ...empresa,
      telefonos,
    }));

    return true;
  }

  function getTelefono(index) {
    const telefono = empresa.telefonos.find((e, i) => i === index);

    if (!telefono) {
      throw new Error("¡Este número de teléfono no existe!");
    }

    return telefono;
  }

  function updateTelefono(index, telefono) {
    const already = empresa.telefonos.find((t, i) => {
      return (
        i !== index &&
        String(t.telefono_numero).replaceAll("-", "") ===
          String(telefono.telefono_numero).replaceAll("-", "")
      );
    });

    if (!!already) {
      throw new Error("¡Este número de teléfono ya está agregado!");
    }

    const telefonos = empresa.telefonos.map((t, i) => {
      if (i === index) {
        return telefono;
      }

      return t;
    });

    setEmpresa((empresa) => ({
      ...empresa,
      telefonos,
    }));

    return true;
  }

  function deleteTelefono(i) {
    if (
      window.confirm(
        "¿Está seguro(a) que desea eliminar este número de teléfono?"
      )
    ) {
      const telefonos = empresa.telefonos.filter((e, index) => index !== i);

      setEmpresa((empresa) => ({
        ...empresa,
        telefonos,
      }));

      return true;
    }

    return false;
  }

  // números de celular
  function addCelular(celular) {
    const already = empresa.celulares.find(
      (cel) =>
        String(cel.celular_numero).replaceAll("-", "") ===
        String(celular.celular_numero).replaceAll("-", "")
    );

    if (!!already) {
      throw new Error("¡Este número de celular ya está agregado!");
    }

    const celulares = empresa.celulares.concat(celular);

    setEmpresa((empresa) => ({
      ...empresa,
      celulares,
    }));

    return true;
  }

  function getCelular(index) {
    const celular = empresa.celulares.find((e, i) => i === index);

    if (!celular) {
      throw new Error("¡Este número de celular no existe!");
    }

    return celular;
  }

  function updateCelular(index, celular) {
    const already = empresa.celulares.find((c, i) => {
      return (
        i !== index &&
        String(c.celular_numero).replaceAll("-", "") ===
          String(celular.celular_numero).replaceAll("-", "")
      );
    });

    if (!!already) {
      throw new Error("¡Este número de celular ya está agregado!");
    }

    const celulares = empresa.celulares.map((c, i) => {
      if (i === index) {
        return celular;
      }

      return c;
    });

    setEmpresa((empresa) => ({
      ...empresa,
      celulares,
    }));

    return true;
  }

  function deleteCelular(i) {
    if (
      window.confirm(
        "¿Está seguro(a) que desea eliminar este número de celular?"
      )
    ) {
      const celulares = empresa.celulares.filter((e, index) => index !== i);

      setEmpresa((empresa) => ({
        ...empresa,
        celulares,
      }));

      return true;
    }

    return false;
  }

  // correos electrónicos
  function addCorreoElectronico(correo) {
    const already = empresa.correos_electronicos.find(
      (cor) =>
        String(cor.correo_electronico_direccion).toLowerCase() ===
        String(correo.correo_electronico_direccion).toLowerCase()
    );

    if (!!already) {
      throw new Error(
        "¡Esta dirección de correo electrónico ya está agregada!"
      );
    }

    const correos_electronicos = empresa.correos_electronicos.concat({
      ...correo,
      correo_electronico_direccion: String(
        correo.correo_electronico_direccion
      ).toLowerCase(),
    });

    setEmpresa((empresa) => ({
      ...empresa,
      correos_electronicos,
    }));

    return true;
  }

  function getCorreoElectronico(index) {
    const correoElectronico = empresa.correos_electronicos.find(
      (e, i) => i === index
    );

    if (!correoElectronico) {
      throw new Error("¡Esta dirección de correo electrónico no existe!");
    }

    return correoElectronico;
  }

  function updateCorreoElectronico(index, correo) {
    const already = empresa.correos_electronicos.find((c, i) => {
      return (
        i !== index &&
        c.correo_electronico_direccion === correo.correo_electronico_direccion
      );
    });

    if (!!already) {
      throw new Error(
        "¡Esta dirección de correo electrónico ya está agregada!"
      );
    }

    const correos_electronicos = empresa.correos_electronicos.map((c, i) => {
      if (i === index) {
        return correo;
      }

      return c;
    });

    setEmpresa((empresa) => ({
      ...empresa,
      correos_electronicos,
    }));

    return true;
  }

  function deleteCorreoElectronico(index) {
    if (
      window.confirm(
        "¿Está seguro(a) que desea eliminar esta dirección de correo electrónico?"
      )
    ) {
      const correos_electronicos = empresa.correos_electronicos.filter(
        (e, i) => index !== i
      );

      setEmpresa((empresa) => ({
        ...empresa,
        correos_electronicos,
      }));

      return true;
    }

    return false;
  }

  // páginas web
  function addPaginaWeb(pagina) {
    const already = empresa.paginas_web.find(
      (pag) => pag.pagina_web_url === pagina.pagina_web_url
    );

    if (!!already) {
      throw new Error("¡Esta dirección de página web ya está agregada!");
    }

    const paginas_web = empresa.paginas_web.concat(pagina);

    setEmpresa((empresa) => ({
      ...empresa,
      paginas_web,
    }));

    return true;
  }

  function getPaginaWeb(index) {
    const paginaWeb = empresa.paginas_web.find((e, i) => i === index);

    if (!paginaWeb) {
      throw new Error("¡Esta dirección de página web no existe!");
    }

    return paginaWeb;
  }

  function updatePaginaWeb(index, pagina) {
    const already = empresa.paginas_web.find((p, i) => {
      return i !== index && p.pagina_web_url === pagina.pagina_web_url;
    });

    if (!!already) {
      throw new Error("¡Esta dirección de página web ya está agregada!");
    }

    const paginas_web = empresa.paginas_web.map((p, i) => {
      if (i === index) {
        return pagina;
      }

      return p;
    });

    setEmpresa((empresa) => ({
      ...empresa,
      paginas_web,
    }));

    return true;
  }

  function deletePaginaWeb(index) {
    if (
      window.confirm(
        "¿Está seguro(a) que desea eliminar esta dirección de página web?"
      )
    ) {
      const paginas_web = empresa.paginas_web.filter((e, i) => i !== index);

      setEmpresa((empresa) => ({
        ...empresa,
        paginas_web,
      }));

      return true;
    }

    return false;
  }

  // encargados
  function addEncargado(encargado) {
    const identificacionIsAlready = empresa.encargados.some(
      (enc) =>
        String(enc.encargado_identificacion) ===
        String(encargado.encargado_identificacion)
    );

    if (!!identificacionIsAlready) {
      throw new Error(
        `¡Ya hay otro encargado que tiene esta identificación: ${encargado.encargado_identificacion}, por favor, pruebe con otra identificación!`
      );
    }

    const rtnIsAlready = empresa.encargados.some(
      (enc) => String(enc.encargado_rtn) === String(encargado.encargado_rtn)
    );

    if (!!rtnIsAlready) {
      throw new Error(
        `¡Ya hay otro encargado que tiene este número de RTN: ${encargado.encargado_rtn}, por favor, pruebe con otro número de RTN!`
      );
    }

    const celularIsAlready = empresa.encargados.some(
      (enc) =>
        String(enc.encargado_celular).replace("-", "") ===
        String(encargado.encargado_celular).replace("-", "")
    );

    if (!!celularIsAlready) {
      throw new Error(
        `¡Ya hay otro encargado que tiene este número de celular: ${encargado.encargado_celular}, por favor, pruebe con otro número de celular!`
      );
    }

    const encargados = empresa.encargados.concat(encargado);

    setEmpresa((empresa) => ({ ...empresa, encargados }));

    return true;
  }

  function getEncargado(index) {
    const encargado = empresa.encargados.find((e, i) => i === index);

    if (!encargado) {
      throw new Error("¡Este encargado no existe!");
    }

    return encargado;
  }

  function updateEncargado(index, encargado) {
    // const already = empresa.encargados.find((e, i) => {
    //   return (
    //     i !== index &&
    //     String(e.encargado_identificacion).replaceAll("-", "") ===
    //       String(encargado.encargado_identificacion).replaceAll("-", "")
    //   );
    // });

    // if (!!already) {
    //   throw new Error("¡El DNI de este encargado ya está agregado!");
    // }

    // const already2 = empresa.encargados.find((e, i) => {
    //   return (
    //     i !== index &&
    //     String(e.encargado_rtn).replaceAll("-", "") ===
    //       String(encargado.encargado_rtn).replaceAll("-", "")
    //   );
    // });

    // if (!!already2) {
    //   throw new Error("¡El RTN de este encargado ya está agregado!");
    // }

    const identificacionIsAlready = empresa.encargados.some(
      (enc, i) =>
        i !== index &&
        String(enc.encargado_identificacion) ===
          String(encargado.encargado_identificacion)
    );

    if (!!identificacionIsAlready) {
      throw new Error(
        `¡Ya hay otro encargado que tiene esta identificación: ${encargado.encargado_identificacion}, por favor, pruebe con otra identificación!`
      );
    }

    const rtnIsAlready = empresa.encargados.some(
      (enc, i) =>
        i !== index &&
        String(enc.encargado_rtn) === String(encargado.encargado_rtn)
    );

    if (!!rtnIsAlready) {
      throw new Error(
        `¡Ya hay otro encargado que tiene este número de RTN: ${encargado.encargado_rtn}, por favor, pruebe con otro número de RTN!`
      );
    }

    const celularIsAlready = empresa.encargados.some(
      (enc, i) =>
        i !== index &&
        String(enc.encargado_celular).replace("-", "") ===
          String(encargado.encargado_celular).replace("-", "")
    );

    if (!!celularIsAlready) {
      throw new Error(
        `¡Ya hay otro encargado que tiene este número de celular: ${encargado.encargado_celular}, por favor, pruebe con otro número de celular!`
      );
    }

    const encargados = empresa.encargados.map((enc, i) => {
      if (i === index) {
        return encargado;
      }

      return enc;
    });

    setEmpresa((empresa) => ({ ...empresa, encargados }));

    return true;
  }

  function deleteEncargado(index) {
    if (window.confirm("¿Está seguro(a) que desea eliminar este encargado?")) {
      const encargados = empresa.encargados.filter((e, i) => i !== index);

      setEmpresa((empresa) => ({
        ...empresa,
        encargados,
      }));

      return true;
    }

    return false;
  }

  // actividades económicas
  function addActividad(actividad) {
    const already = empresa.actividades_economicas.find(
      (act) => act.actividad_economica_id === actividad.actividad_economica_id
    );

    if (!!already) {
      throw new Error("¡Esta actividad económica ya está agregada!");
    }

    const actividades_economicas =
      empresa.actividades_economicas.concat(actividad);

    setEmpresa((empresa) => ({
      ...empresa,
      actividades_economicas,
    }));

    return true;
  }

  function getActividad(index) {
    const actividad = empresa.actividades_economicas.find(
      (a, i) => i === index
    );

    if (!actividad) {
      throw new Error("¡Esta actividad economica no existe!");
    }

    return actividad;
  }

  function updateActividad(index, actividad) {
    const already = empresa.actividades_economicas.find((e, i) => {
      return (
        i !== index &&
        e.actividad_economica_id === actividad.actividad_economica_id
      );
    });

    if (!!already) {
      throw new Error("¡Esta actividad económica ya está agregada!");
    }

    const actividades_economicas = empresa.actividades_economicas.map(
      (p, i) => {
        if (i === index) {
          return actividad;
        }

        return p;
      }
    );

    setEmpresa((empresa) => ({
      ...empresa,
      actividades_economicas,
    }));

    return true;
  }

  function deleteActividad(index) {
    if (
      window.confirm(
        "¿Está seguro(a) que desea eliminar esta actividad económica?"
      )
    ) {
      const actividades_economicas = empresa.actividades_economicas.filter(
        (e, i) => i !== index
      );

      setEmpresa((empresa) => ({
        ...empresa,
        actividades_economicas,
      }));

      return true;
    }

    return false;
  }

  // importaciones

  function getImportacion(index) {
    const importacion = empresa.importaciones.find((imp, i) => i === index);

    if (!importacion) {
      throw new Error("¡Esta importación no existe!");
    }

    return importacion;
  }

  function updateImportacion(index, importacion) {
    const already = empresa.importaciones.find((imp, i) => {
      return (
        i !== index &&
        imp.importacion_detalles === importacion.importacion_detalles
      );
    });

    if (!!already) {
      throw new Error("¡Esta importación ya está agregada!");
    }

    const importaciones = empresa.importaciones.map((imp, i) => {
      if (i === index) {
        return importacion;
      }

      return imp;
    });

    setEmpresa((empresa) => ({
      ...empresa,
      importaciones,
    }));

    return true;
  }

  function deleteImportacion(index) {
    if (
      window.confirm("¿Está seguro(a) que desea eliminar esta importación?")
    ) {
      const importaciones = empresa.importaciones.filter(
        (imp, i) => i !== index
      );

      setEmpresa((empresa) => ({
        ...empresa,
        importaciones,
      }));

      return true;
    }

    return false;
  }

  // exportaciones
  function addExportacion(exportacion) {
    const already = empresa.exportaciones.find(
      (exp) => exp.exportacion_detalles === exportacion.exportacion_detalles
    );

    if (!!already) {
      throw new Error("¡Esta exportación ya está agregada!");
    }

    const exportaciones = empresa.exportaciones.concat(exportacion);

    setEmpresa((empresa) => ({
      ...empresa,
      exportaciones,
    }));

    return true;
  }

  function getExportacion(index) {
    const exportacion = empresa.exportaciones.find((exp, i) => i === index);

    if (!exportacion) {
      throw new Error("¡Esta exportación no existe!");
    }

    return exportacion;
  }

  function updateExportacion(index, exportacion) {
    const already = empresa.exportaciones.find((exp, i) => {
      return (
        i !== index &&
        exp.exportacion_detalles === exportacion.exportacion_detalles
      );
    });

    if (!!already) {
      throw new Error("¡Esta exportación ya está agregada!");
    }

    const exportaciones = empresa.exportaciones.map((exp, i) => {
      if (i === index) {
        return exportacion;
      }

      return exp;
    });

    setEmpresa((empresa) => ({
      ...empresa,
      exportaciones,
    }));

    return true;
  }

  function deleteExportacion(index) {
    if (
      window.confirm("¿Está seguro(a) que desea eliminar esta exportación?")
    ) {
      const exportaciones = empresa.exportaciones.filter(
        (exp, i) => i !== index
      );

      setEmpresa((empresa) => ({
        ...empresa,
        exportaciones,
      }));

      return true;
    }

    return false;
  }

  function addImportacion(importacion) {
    const already = empresa.importaciones.find(
      (exp) => exp.importacion_detalles === importacion.importacion_detalles
    );

    if (!!already) {
      throw new Error("¡Esta importación ya está agregada!");
    }

    const importaciones = empresa.importaciones.concat(importacion);

    setEmpresa((empresa) => ({
      ...empresa,
      importaciones,
    }));

    return true;
  }

  function reset() {
    const confirmed = window.confirm(
      "Se perderá el progreso ¿Desea reiniciar el formulario?"
    );

    if (confirmed) {
      setEmpresa(initialState);

      return navigate("/empresa/datos-generales");
    }
  }

  async function createSolicitud({ files = [] }) {
    const confirmed = window.confirm(
      "Esta solicitud de inscripción está sujeta a revisión por el personal administrativo de la Cámara de Comercio e Industrias de Atlántida. Para su aprobación, el propietario o persona autorizada, deberá presentar en las oficinas de la Cámara de Comercio e Industrias de Atlántida la escritura de constitución original, fotocopia de la cédula de identidad (propietario o socios), fotocopia del RTN (propietario, socios o empresa) y realizar el pago por inscripción de acuerdo al capital mínimo establecido en su escritura de constitución."
    );

    if (!confirmed) return;

    return await createSolicitudMutation.mutateAsync({ files });
  }

  return (
    <EmpresaDataContext.Provider
      value={{
        empresa,
        setEmpresa,
        addEstablecimiento, // establecimientos
        getEstablecimiento,
        updateEstablecimiento,
        deleteEstablecimiento,
        addTelefono, // telefonos
        getTelefono,
        updateTelefono,
        deleteTelefono,
        addCelular, // celular
        getCelular,
        updateCelular,
        deleteCelular,
        addCorreoElectronico, // correos electrónicos
        getCorreoElectronico,
        updateCorreoElectronico,
        deleteCorreoElectronico,
        addPaginaWeb, // paginas web
        getPaginaWeb,
        updatePaginaWeb,
        deletePaginaWeb,
        addEncargado, // encargados
        getEncargado,
        updateEncargado,
        deleteEncargado,
        addActividad, // actividades economicas
        getActividad,
        updateActividad,
        deleteActividad,
        addExportacion, // exportaciones
        getExportacion,
        updateExportacion,
        deleteExportacion,
        addImportacion, // importaciones
        getImportacion,
        updateImportacion,
        deleteImportacion,
        createSolicitud,
        isSubmitting: createSolicitudMutation.isLoading,
        reset, // limpiar el form
      }}
    >
      {props.children}
    </EmpresaDataContext.Provider>
  );
}

export function useEmpresa() {
  const context = useContext(EmpresaDataContext);

  if (!context) {
    throw new Error("EmpresaDataProvider is missing");
  }

  return context;
}
